import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useNotification } from '../components/Notification';
import Spinner from '../components/Spinner';
import './ApiTokens.css';

function ApiTokens({ tenantId }) {
  const { showNotification } = useNotification();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newToken, setNewToken] = useState({ name: '', expiryDays: 90 });
  const [createdToken, setCreatedToken] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (tenantId) {
      loadTokens();
    } else {
      setLoading(false);
    }
  }, [tenantId]);

  const loadTokens = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api-tokens', null);
      setTokens(data || []);
    } catch (error) {
      console.error('Error loading API tokens:', error);
      showNotification('Fehler beim Laden der API-Tokens', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateToken = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/api-tokens', newToken);
      if (response.token) {
        setCreatedToken(response);
        setShowCreateModal(false);
        setNewToken({ name: '', expiryDays: 90 });
        await loadTokens();
        showNotification('API-Token erfolgreich erstellt', 'success');
      }
    } catch (error) {
      console.error('Error creating API token:', error);
      showNotification(error.message || 'Fehler beim Erstellen des API-Tokens', 'error');
    }
  };

  const handleDeleteToken = async (id) => {
    if (!window.confirm('Möchten Sie diesen API-Token wirklich löschen?')) {
      return;
    }

    setDeletingId(id);
    try {
      await api.delete(`/api-tokens/${id}`);
      await loadTokens();
      showNotification('API-Token erfolgreich gelöscht', 'success');
    } catch (error) {
      console.error('Error deleting API token:', error);
      showNotification('Fehler beim Löschen des API-Tokens', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      showNotification('Token in Zwischenablage kopiert', 'success');
    }).catch(() => {
      showNotification('Fehler beim Kopieren', 'error');
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="api-tokens">
      <div className="api-tokens-header">
        <h1>API-Tokens</h1>
        <button 
          className="btn-primary" 
          onClick={() => setShowCreateModal(true)}
        >
          Neuer API-Token
        </button>
      </div>

      <div className="api-tokens-info">
        <p>
          API-Tokens ermöglichen es Ihnen, programmatisch auf die GarageDesk API zuzugreifen. 
          Verwenden Sie diese Tokens für die Authentifizierung in Ihren API-Anfragen.
        </p>
        <p>
          <strong>Wichtig:</strong> Speichern Sie den Token sicher, da er nach der Erstellung nicht erneut angezeigt wird.
        </p>
      </div>

      {createdToken && (
        <div className="token-created-modal">
          <div className="token-created-content">
            <h2>API-Token erstellt</h2>
            <p>Bitte kopieren Sie den Token jetzt. Er wird nicht erneut angezeigt.</p>
            <div className="token-display">
              <code>{createdToken.token}</code>
              <button 
                className="btn-secondary"
                onClick={() => copyToClipboard(createdToken.token)}
              >
                Kopieren
              </button>
            </div>
            <div className="token-info">
              <p><strong>Name:</strong> {createdToken.tokenInfo.name}</p>
              <p><strong>Erstellt:</strong> {formatDate(createdToken.tokenInfo.createdAt)}</p>
              <p><strong>Läuft ab:</strong> {formatDate(createdToken.tokenInfo.expiresAt)}</p>
            </div>
            <button 
              className="btn-primary"
              onClick={() => setCreatedToken(null)}
            >
              Schließen
            </button>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Neuer API-Token</h2>
            <form onSubmit={handleCreateToken}>
              <div className="form-group">
                <label htmlFor="token-name">Name *</label>
                <input
                  type="text"
                  id="token-name"
                  value={newToken.name}
                  onChange={(e) => setNewToken({ ...newToken, name: e.target.value })}
                  placeholder="z.B. Production API, Development API"
                  required
                  maxLength={100}
                />
              </div>
              <div className="form-group">
                <label htmlFor="expiry-days">Ablaufzeit (Tage)</label>
                <input
                  type="number"
                  id="expiry-days"
                  value={newToken.expiryDays}
                  onChange={(e) => setNewToken({ ...newToken, expiryDays: parseInt(e.target.value) || 0 })}
                  min="0"
                  placeholder="0 = nie ablaufen"
                />
                <small>0 = Token läuft nie ab (nicht empfohlen)</small>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Abbrechen
                </button>
                <button type="submit" className="btn-primary">
                  Erstellen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {tokens.length === 0 ? (
        <div className="empty-state">
          <p>Noch keine API-Tokens erstellt.</p>
        </div>
      ) : (
        <div className="tokens-list">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Erstellt</th>
                <th>Zuletzt verwendet</th>
                <th>Läuft ab</th>
                <th>Status</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {tokens.map(token => (
                <tr key={token.id} className={token.isExpired ? 'expired' : ''}>
                  <td>{token.name}</td>
                  <td>{formatDate(token.createdAt)}</td>
                  <td>{formatDate(token.lastUsed) || 'Nie'}</td>
                  <td>{formatDate(token.expiresAt)}</td>
                  <td>
                    {token.isExpired ? (
                      <span className="status-badge expired">Abgelaufen</span>
                    ) : (
                      <span className="status-badge active">Aktiv</span>
                    )}
                  </td>
                  <td>
                    <button
                      className="btn-small btn-danger"
                      onClick={() => handleDeleteToken(token.id)}
                      disabled={deletingId === token.id}
                    >
                      {deletingId === token.id ? 'Löschen...' : 'Löschen'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ApiTokens;

